import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Compass,
  FolderOpen,
  BookOpen,
  Leaf,
  Activity,
  FileCheck2,
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  Search,
  CheckCircle2,
  XCircle,
  Lock,
  Unlock,
  Sparkles,
  Info,
  Pill,
  HeartPulse,
  Settings2,
  Layers,
  LayoutGrid,
  ArrowRight,
  BookA,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetExploreCategoriesQuery,
  useCreateExploreCategoryMutation,
  useUpdateExploreCategoryMutation,
  useDeleteExploreCategoryMutation,
  useGetDriveResourcesQuery,
  useCreateDriveResourceMutation,
  useUpdateDriveResourceMutation,
  useDeleteDriveResourceMutation,
  useGetDictionaryTermsQuery,
  useCreateDictionaryTermMutation,
  useUpdateDictionaryTermMutation,
  useDeleteDictionaryTermMutation,
  useGetDravyagunaHerbsQuery,
  useCreateDravyagunaHerbMutation,
  useUpdateDravyagunaHerbMutation,
  useDeleteDravyagunaHerbMutation,
  useGetChikitsaProtocolsQuery,
  useCreateChikitsaProtocolMutation,
  useUpdateChikitsaProtocolMutation,
  useDeleteChikitsaProtocolMutation,
  useGetExploreSubmissionsQuery,
  useReviewExploreSubmissionMutation,
  ExploreCategory,
  ExploreDriveResource,
  ExploreDictionaryTerm,
  ExploreDravyagunaHerb,
  ExploreChikitsaProtocol,
  ExploreSubmission,
} from "@/redux/slices/exploreApi";

interface ExplorePageProps {
  defaultSection?: "overview" | "drive" | "dictionary" | "dravya" | "chikitsa" | "submissions";
}

export default function ExplorePage({ defaultSection }: ExplorePageProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine current active section from URL or prop
  const getSectionFromPath = (): "overview" | "drive" | "dictionary" | "dravya" | "chikitsa" | "submissions" => {
    if (location.pathname.endsWith("/drive")) return "drive";
    if (location.pathname.endsWith("/dictionary") || location.pathname === "/dictionary") return "dictionary";
    if (location.pathname.endsWith("/dravya")) return "dravya";
    if (location.pathname.endsWith("/chikitsa")) return "chikitsa";
    if (location.pathname.endsWith("/submissions")) return "submissions";
    return defaultSection || "overview";
  };

  const currentSection = getSectionFromPath();

  const handleNavigateSection = (sec: string) => {
    if (sec === "overview") navigate("/explore");
    else navigate(`/explore/${sec}`);
  };

  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [dictCategoryFilter, setDictCategoryFilter] = useState("all");
  const [dictLetterFilter, setDictLetterFilter] = useState("ALL");
  const [doshaFilter, setDoshaFilter] = useState("all");
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState("PENDING");

  // =========================================================================
  // RTK QUERY HOOKS
  // =========================================================================
  const { data: catData, isLoading: loadingCats, refetch: refetchCats } = useGetExploreCategoriesQuery();
  const categories: ExploreCategory[] = catData?.data || [];

  const { data: driveData, isLoading: loadingDrive, refetch: refetchDrive } = useGetDriveResourcesQuery({
    yearKey: yearFilter === "all" ? undefined : yearFilter,
    search: search.trim() || undefined,
  });

  const { data: dictData, isLoading: loadingDict, refetch: refetchDict } = useGetDictionaryTermsQuery({
    letter: dictLetterFilter === "ALL" ? undefined : dictLetterFilter,
    category: dictCategoryFilter === "all" ? undefined : dictCategoryFilter,
    search: search.trim() || undefined,
  });

  const { data: dravyaData, isLoading: loadingDravya, refetch: refetchDravya } = useGetDravyagunaHerbsQuery({
    search: search.trim() || undefined,
  });

  const { data: chikitsaData, isLoading: loadingChikitsa, refetch: refetchChikitsa } = useGetChikitsaProtocolsQuery({
    dosha: doshaFilter === "all" ? undefined : doshaFilter,
    search: search.trim() || undefined,
  });

  const { data: subData, isLoading: loadingSubs, refetch: refetchSubs } = useGetExploreSubmissionsQuery({
    status: submissionStatusFilter === "ALL" ? undefined : submissionStatusFilter,
  });

  // Mutations
  const [createCategory, { isLoading: isCreatingCat }] = useCreateExploreCategoryMutation();
  const [updateCategory, { isLoading: isUpdatingCat }] = useUpdateExploreCategoryMutation();
  const [deleteCategory] = useDeleteExploreCategoryMutation();

  const [createDriveResource, { isLoading: isCreatingDrive }] = useCreateDriveResourceMutation();
  const [updateDriveResource, { isLoading: isUpdatingDrive }] = useUpdateDriveResourceMutation();
  const [deleteDriveResource] = useDeleteDriveResourceMutation();

  const [createDictionaryTerm, { isLoading: isCreatingDict }] = useCreateDictionaryTermMutation();
  const [updateDictionaryTerm, { isLoading: isUpdatingDict }] = useUpdateDictionaryTermMutation();
  const [deleteDictionaryTerm] = useDeleteDictionaryTermMutation();

  const [createDravyagunaHerb, { isLoading: isCreatingDravya }] = useCreateDravyagunaHerbMutation();
  const [updateDravyagunaHerb, { isLoading: isUpdatingDravya }] = useUpdateDravyagunaHerbMutation();
  const [deleteDravyagunaHerb] = useDeleteDravyagunaHerbMutation();

  const [createChikitsaProtocol, { isLoading: isCreatingChikitsa }] = useCreateChikitsaProtocolMutation();
  const [updateChikitsaProtocol, { isLoading: isUpdatingChikitsa }] = useUpdateChikitsaProtocolMutation();
  const [deleteChikitsaProtocol] = useDeleteChikitsaProtocolMutation();

  const [reviewSubmission] = useReviewExploreSubmissionMutation();

  // =========================================================================
  // 0. DYNAMIC CATEGORY DIALOG STATE
  // =========================================================================
  const [isCatDialogOpen, setIsCatDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExploreCategory | null>(null);
  const [catForm, setCatForm] = useState({
    title: "",
    tagLine: "",
    description: "",
    categoryType: "DRIVE" as "DRIVE" | "DICTIONARY" | "DRAVYA" | "CHIKITSA" | "CUSTOM",
    icon: "FolderOpen",
    color: "#059669",
    bgColor: "#ECFDF5",
    badgeText: "",
    freeLimit: 5,
    displayOrder: 0,
    isActive: true,
  });

  const handleOpenAddCategory = () => {
    setEditingCategory(null);
    setCatForm({
      title: "",
      tagLine: "",
      description: "",
      categoryType: "DRIVE",
      icon: "FolderOpen",
      color: "#059669",
      bgColor: "#ECFDF5",
      badgeText: "",
      freeLimit: 5,
      displayOrder: categories.length + 1,
      isActive: true,
    });
    setIsCatDialogOpen(true);
  };

  const handleOpenEditCategory = (cat: ExploreCategory) => {
    setEditingCategory(cat);
    setCatForm({
      title: cat.title,
      tagLine: cat.tagLine || "",
      description: cat.description || "",
      categoryType: cat.categoryType,
      icon: cat.icon || "Compass",
      color: cat.color || "#059669",
      bgColor: cat.bgColor || "#ECFDF5",
      badgeText: cat.badgeText || "",
      freeLimit: cat.freeLimit !== undefined ? cat.freeLimit : 5,
      displayOrder: cat.displayOrder || 0,
      isActive: Boolean(cat.isActive),
    });
    setIsCatDialogOpen(true);
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!catForm.title.trim()) {
      toast.error("Category Title is required.");
      return;
    }
    try {
      if (editingCategory) {
        await updateCategory({ id: editingCategory.id, ...catForm }).unwrap();
        toast.success("Category updated successfully!");
      } else {
        await createCategory(catForm).unwrap();
        toast.success("New dynamic category created!");
      }
      setIsCatDialogOpen(false);
      refetchCats();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save category");
    }
  };

  const handleDeleteCategory = async (id: string, title: string) => {
    if (!window.confirm(`Delete category "${title}"?`)) return;
    try {
      await deleteCategory(id).unwrap();
      toast.success("Category deleted");
      refetchCats();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete category");
    }
  };

  // =========================================================================
  // 1. DRIVE DIALOG STATE
  // =========================================================================
  const [isDriveDialogOpen, setIsDriveDialogOpen] = useState(false);
  const [editingDrive, setEditingDrive] = useState<ExploreDriveResource | null>(null);
  const [driveForm, setDriveForm] = useState({
    title: "",
    tagLine: "",
    subjectName: "",
    yearName: "1st Year BAMS",
    yearKey: "1st",
    resourceType: "Google Drive Folder",
    fileCountOrSize: "15 PDFs",
    description: "",
    driveUrl: "",
    color: "#0284C7",
    icon: "folder-google-drive",
    isPopular: false,
  });

  const handleOpenAddDrive = () => {
    setEditingDrive(null);
    setDriveForm({
      title: "",
      tagLine: "",
      subjectName: "",
      yearName: "1st Year BAMS",
      yearKey: "1st",
      resourceType: "Google Drive Folder",
      fileCountOrSize: "Folder",
      description: "",
      driveUrl: "",
      color: "#0284C7",
      icon: "folder-google-drive",
      isPopular: false,
    });
    setIsDriveDialogOpen(true);
  };

  const handleOpenEditDrive = (item: ExploreDriveResource) => {
    setEditingDrive(item);
    setDriveForm({
      title: item.title,
      tagLine: item.tagLine || "",
      subjectName: item.subjectName,
      yearName: item.yearName,
      yearKey: item.yearKey || "1st",
      resourceType: item.resourceType || "Google Drive Folder",
      fileCountOrSize: item.fileCountOrSize || "Folder",
      description: item.description || "",
      driveUrl: item.driveUrl,
      color: item.color || "#0284C7",
      icon: item.icon || "folder-google-drive",
      isPopular: Boolean(item.isPopular),
    });
    setIsDriveDialogOpen(true);
  };

  const handleSaveDrive = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!driveForm.title.trim() || !driveForm.driveUrl.trim() || !driveForm.subjectName.trim()) {
      toast.error("Please fill in Title, Subject Name, and Drive URL.");
      return;
    }
    try {
      if (editingDrive) {
        await updateDriveResource({ id: editingDrive.id, ...driveForm }).unwrap();
        toast.success("Drive resource updated successfully!");
      } else {
        await createDriveResource(driveForm).unwrap();
        toast.success("New Drive resource added successfully!");
      }
      setIsDriveDialogOpen(false);
      refetchDrive();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save Drive resource");
    }
  };

  const handleDeleteDrive = async (id: string, title: string) => {
    if (!window.confirm(`Delete drive resource "${title}"?`)) return;
    try {
      await deleteDriveResource(id).unwrap();
      toast.success("Drive resource deleted");
      refetchDrive();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete");
    }
  };

  // =========================================================================
  // 2. DICTIONARY DIALOG STATE
  // =========================================================================
  const [isDictDialogOpen, setIsDictDialogOpen] = useState(false);
  const [editingDict, setEditingDict] = useState<ExploreDictionaryTerm | null>(null);
  const [dictForm, setDictForm] = useState({
    termSanskrit: "",
    termEnglish: "",
    phonetic: "",
    category: "Sharira Rachana",
    definition: "",
    classicalReference: "",
    shloka: "",
  });

  const handleOpenAddDict = () => {
    setEditingDict(null);
    setDictForm({
      termSanskrit: "",
      termEnglish: "",
      phonetic: "",
      category: "Sharira Rachana",
      definition: "",
      classicalReference: "",
      shloka: "",
    });
    setIsDictDialogOpen(true);
  };

  const handleOpenEditDict = (item: ExploreDictionaryTerm) => {
    setEditingDict(item);
    setDictForm({
      termSanskrit: item.termSanskrit,
      termEnglish: item.termEnglish,
      phonetic: item.phonetic || "",
      category: item.category || "Sharira Rachana",
      definition: item.definition,
      classicalReference: item.classicalReference || "",
      shloka: item.shloka || "",
    });
    setIsDictDialogOpen(true);
  };

  const handleSaveDict = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dictForm.termSanskrit.trim() || !dictForm.termEnglish.trim() || !dictForm.definition.trim()) {
      toast.error("Please fill in Sanskrit Term, English Term, and Definition.");
      return;
    }
    try {
      if (editingDict) {
        await updateDictionaryTerm({ id: editingDict.id, ...dictForm }).unwrap();
        toast.success("Dictionary term updated!");
      } else {
        await createDictionaryTerm(dictForm).unwrap();
        toast.success("Dictionary term created!");
      }
      setIsDictDialogOpen(false);
      refetchDict();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save dictionary term");
    }
  };

  const handleDeleteDict = async (id: string, term: string) => {
    if (!window.confirm(`Delete dictionary term "${term}"?`)) return;
    try {
      await deleteDictionaryTerm(id).unwrap();
      toast.success("Dictionary term deleted");
      refetchDict();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete");
    }
  };

  // =========================================================================
  // 3. DRAVYA HERBOLOGY DIALOG STATE
  // =========================================================================
  const [isDravyaDialogOpen, setIsDravyaDialogOpen] = useState(false);
  const [editingDravya, setEditingDravya] = useState<ExploreDravyagunaHerb | null>(null);
  const [dravyaTab, setDravyaTab] = useState("basic");

  const [prayogEntries, setPrayogEntries] = useState<Array<{ id: number; title: string; formulation: string }>>([
    { id: 1, title: "In Vatarakta / Gout", formulation: "Guduchi kwatha with Guggulu or Eranda taila" },
    { id: 2, title: "In Jwara (Chronic Fever)", formulation: "Guduchi swarasa 10-20 ml with Madhu (Honey)" },
    { id: 3, title: "In Prameha (Diabetes)", formulation: "Guduchi churna with Haridra and Amalaki swarasa" },
  ]);

  const [dravyaForm, setDravyaForm] = useState({
    sanskritName: "",
    imageUrl: "",
    botanicalName: "",
    family: "",
    pradhanKarma: "",
    partUsed: "",
    synonyms: "",
    commonNames: "",
    rasa: "",
    guna: "",
    virya: "Sheeta",
    vipaka: "Madhura",
    prabhava: "",
    doshaKarma: "",
    generalKarma: "",
    karma: "",
    habit: "",
    morphologyRoot: "",
    morphologyStem: "",
    morphologyLeaf: "",
    morphologyFlower: "",
    inflorescence: "",
    morphologyFruit: "",
    morphologySeed: "",
    majorIndications: "",
    classicalDosage: "",
    phytoConstituents: "",
    vishishtYoga: "",
    majorFormulations: "",
    isPopular: false,
  });

  const handleAddPrayogEntry = () => {
    setPrayogEntries([...prayogEntries, { id: Date.now(), title: "", formulation: "" }]);
  };

  const handleRemovePrayogEntry = (id: number) => {
    if (prayogEntries.length <= 1) return;
    setPrayogEntries(prayogEntries.filter((p) => p.id !== id));
  };

  const handleUpdatePrayogEntry = (id: number, field: "title" | "formulation", value: string) => {
    setPrayogEntries(prayogEntries.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };

  const handleOpenAddDravya = () => {
    setEditingDravya(null);
    setDravyaTab("basic");
    setPrayogEntries([
      { id: 1, title: "In Vatarakta / Gout", formulation: "Guduchi kwatha with Guggulu" },
      { id: 2, title: "In Jwara (Fever)", formulation: "Guduchi swarasa with Madhu" },
      { id: 3, title: "In Prameha (Diabetes)", formulation: "Guduchi churna with Haridra and Amalaki" },
    ]);
    setDravyaForm({
      sanskritName: "",
      imageUrl: "",
      botanicalName: "",
      family: "",
      pradhanKarma: "",
      partUsed: "Whole Plant / Roots",
      synonyms: "",
      commonNames: "",
      rasa: "Tikta, Kashaya, Madhura",
      guna: "Laghu, Ruksha",
      virya: "Sheeta",
      vipaka: "Madhura",
      prabhava: "",
      doshaKarma: "Tridoshashamaka (balances Vata, Pitta, Kapha)",
      generalKarma: "Deepana, Pachana, Rasayana",
      karma: "Rasayana, Medhya",
      habit: "Perennial Climber / Shrub",
      morphologyRoot: "",
      morphologyStem: "",
      morphologyLeaf: "",
      morphologyFlower: "",
      inflorescence: "",
      morphologyFruit: "",
      morphologySeed: "",
      majorIndications: "Jwara, Prameha, Rasayana, Dhatukshaya",
      classicalDosage: "3-6 gm Churna, 10-20 ml Swarasa",
      phytoConstituents: "Giloin, Tinosporine, Berberine",
      vishishtYoga: "Amritarishta, Guduchi Taila, Sanshamani Vati",
      majorFormulations: "Sanshamani Vati, Guduchyadi Kwatha",
      isPopular: false,
    });
    setIsDravyaDialogOpen(true);
  };

  const handleOpenEditDravya = (item: ExploreDravyagunaHerb) => {
    setEditingDravya(item);
    setDravyaTab("basic");
    const existing: Array<{ id: number; title: string; formulation: string }> = [];
    if (item.amayikPrayog1) existing.push({ id: 1, title: "Entry 1", formulation: item.amayikPrayog1 });
    if (item.amayikPrayog2) existing.push({ id: 2, title: "Entry 2", formulation: item.amayikPrayog2 });
    if (item.amayikPrayog3) existing.push({ id: 3, title: "Entry 3", formulation: item.amayikPrayog3 });
    if (existing.length === 0) existing.push({ id: 1, title: "Entry 1", formulation: "" });
    setPrayogEntries(existing);

    setDravyaForm({
      sanskritName: item.sanskritName,
      imageUrl: item.imageUrl || "",
      botanicalName: item.botanicalName,
      family: item.family,
      pradhanKarma: item.pradhanKarma || "",
      partUsed: item.partUsed || "",
      synonyms: item.synonyms || "",
      commonNames: item.commonNames || "",
      rasa: item.rasa,
      guna: item.guna,
      virya: item.virya,
      vipaka: item.vipaka,
      prabhava: item.prabhava || "",
      doshaKarma: item.doshaKarma || "",
      generalKarma: item.generalKarma || "",
      karma: item.karma,
      habit: item.habit || "",
      morphologyRoot: item.morphologyRoot || "",
      morphologyStem: item.morphologyStem || "",
      morphologyLeaf: item.morphologyLeaf || "",
      morphologyFlower: item.morphologyFlower || "",
      inflorescence: item.inflorescence || "",
      morphologyFruit: item.morphologyFruit || "",
      morphologySeed: item.morphologySeed || "",
      majorIndications: item.majorIndications,
      classicalDosage: item.classicalDosage,
      phytoConstituents: item.phytoConstituents || "",
      vishishtYoga: item.vishishtYoga || "",
      majorFormulations: item.majorFormulations || "",
      isPopular: Boolean(item.isPopular),
    });
    setIsDravyaDialogOpen(true);
  };

  const handleSaveDravya = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dravyaForm.sanskritName.trim() || !dravyaForm.botanicalName.trim() || !dravyaForm.family.trim()) {
      toast.error("Please fill in Plant Sanskrit Name, Botanical Name, and Family.");
      return;
    }

    const payload = {
      ...dravyaForm,
      amayikPrayog1: prayogEntries[0] ? `${prayogEntries[0].title}: ${prayogEntries[0].formulation}` : null,
      amayikPrayog2: prayogEntries[1] ? `${prayogEntries[1].title}: ${prayogEntries[1].formulation}` : null,
      amayikPrayog3: prayogEntries[2] ? `${prayogEntries[2].title}: ${prayogEntries[2].formulation}` : null,
    };

    try {
      if (editingDravya) {
        await updateDravyagunaHerb({ id: editingDravya.id, ...payload }).unwrap();
        toast.success("Dravya Herbology entry updated!");
      } else {
        await createDravyagunaHerb(payload).unwrap();
        toast.success("New Dravya Herbology entry created!");
      }
      setIsDravyaDialogOpen(false);
      refetchDravya();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save herb");
    }
  };

  const handleDeleteDravya = async (id: string, name: string) => {
    if (!window.confirm(`Delete plant "${name}"?`)) return;
    try {
      await deleteDravyagunaHerb(id).unwrap();
      toast.success("Dravya herb removed");
      refetchDravya();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete");
    }
  };

  // =========================================================================
  // 4. CHIKITSA PROTOCOLS DIALOG STATE
  // =========================================================================
  const [isChikitsaDialogOpen, setIsChikitsaDialogOpen] = useState(false);
  const [editingChikitsa, setEditingChikitsa] = useState<ExploreChikitsaProtocol | null>(null);
  const [chikitsaTab, setChikitsaTab] = useState("basic");

  const [aushadhiList, setAushadhiList] = useState<Array<{ id: number; name: string; dose: string; anupan: string; kala: string }>>([
    { id: 1, name: "Sudarshan Churna / Vati", dose: "500 mg BD", anupan: "Ushnodaka (Warm Water)", kala: "Pragbhakta" },
    { id: 2, name: "Tribhuvana Kirti Rasa", dose: "125-250 mg BD", anupan: "Adraka Swarasa & Madhu", kala: "Madhyabhakta" },
    { id: 3, name: "Amritarishta", dose: "20 ml with equal water", anupan: "Pashchadbhakta (After food)", kala: "Twice daily" },
  ]);

  const [chikitsaForm, setChikitsaForm] = useState({
    diseaseNameSanskrit: "",
    diseaseNameEnglish: "",
    diagramUrl: "",
    doshaPredominance: "Vata-Pitta",
    dushya: "Rasa, Rakta, Mamsa",
    parichay: "",
    paribhasha: "",
    sampraptiSummary: "",
    chikitsaSutra: "",
    visheshChikitsa: "",
    shodhanaTherapy: "",
    modernTreatment: "",
    pathya: "",
    apathya: "",
    referenceSamhita: "Charaka Samhita",
    isPopular: false,
  });

  const handleAddAushadhi = () => {
    setAushadhiList([...aushadhiList, { id: Date.now(), name: "", dose: "", anupan: "", kala: "" }]);
  };

  const handleRemoveAushadhi = (id: number) => {
    if (aushadhiList.length <= 1) return;
    setAushadhiList(aushadhiList.filter((a) => a.id !== id));
  };

  const handleUpdateAushadhi = (id: number, field: string, value: string) => {
    setAushadhiList(aushadhiList.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
  };

  const handleOpenAddChikitsa = () => {
    setEditingChikitsa(null);
    setChikitsaTab("basic");
    setAushadhiList([
      { id: 1, name: "Sudarshan Churna", dose: "500 mg BD", anupan: "Warm Water", kala: "Pragbhakta" },
      { id: 2, name: "Tribhuvana Kirti Rasa", dose: "250 mg BD", anupan: "Ginger juice + Honey", kala: "Madhyabhakta" },
    ]);
    setChikitsaForm({
      diseaseNameSanskrit: "",
      diseaseNameEnglish: "",
      diagramUrl: "",
      doshaPredominance: "Vata-Kapha",
      dushya: "Rasa, Rakta, Meda",
      parichay: "",
      paribhasha: "",
      sampraptiSummary: "Agni mandya leading to Ama formation and Srotorodha.",
      chikitsaSutra: "Langhanam Svedanam Kalo Yavagyo Tikto Raso Hitah",
      visheshChikitsa: "Vamana or Virechana based on Rogi Bala and Dosha.",
      shodhanaTherapy: "Mrudu Virechana",
      modernTreatment: "Fluid resuscitation, symptomatic antipyretics, antimicrobial therapy if indicated.",
      pathya: "Mudga Yusha, Lajamanda, Warm boiled water, Rest",
      apathya: "Guru, Snigdha, Sheeta food, Day sleeping, Vyayama",
      referenceSamhita: "Charaka Samhita Chikitsa Sthana",
      isPopular: false,
    });
    setIsChikitsaDialogOpen(true);
  };

  const handleOpenEditChikitsa = (item: ExploreChikitsaProtocol) => {
    setEditingChikitsa(item);
    setChikitsaTab("basic");
    setAushadhiList([
      {
        id: 1,
        name: item.shamanaAushadhi || "",
        dose: item.dosageDetails || "",
        anupan: item.anupan || "",
        kala: item.sevanKala || "",
      },
    ]);
    setChikitsaForm({
      diseaseNameSanskrit: item.diseaseNameSanskrit,
      diseaseNameEnglish: item.diseaseNameEnglish,
      diagramUrl: item.diagramUrl || "",
      doshaPredominance: item.doshaPredominance,
      dushya: item.dushya || "",
      parichay: item.parichay || "",
      paribhasha: item.paribhasha || "",
      sampraptiSummary: item.sampraptiSummary,
      chikitsaSutra: item.chikitsaSutra,
      visheshChikitsa: item.visheshChikitsa || "",
      shodhanaTherapy: item.shodhanaTherapy || "",
      modernTreatment: item.modernTreatment || "",
      pathya: item.pathya,
      apathya: item.apathya,
      referenceSamhita: item.referenceSamhita,
      isPopular: Boolean(item.isPopular),
    });
    setIsChikitsaDialogOpen(true);
  };

  const handleSaveChikitsa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chikitsaForm.diseaseNameSanskrit.trim() || !chikitsaForm.chikitsaSutra.trim()) {
      toast.error("Please fill in Disease Sanskrit Name and Chikitsa Sutra.");
      return;
    }

    const compiledAushadhi = aushadhiList.map((a) => a.name).filter(Boolean).join(", ");
    const compiledDose = aushadhiList.map((a) => `${a.name}: ${a.dose}`).filter(Boolean).join("; ");
    const compiledAnupan = aushadhiList.map((a) => `${a.name}: ${a.anupan}`).filter(Boolean).join("; ");
    const compiledKala = aushadhiList.map((a) => `${a.name}: ${a.kala}`).filter(Boolean).join("; ");

    const payload = {
      ...chikitsaForm,
      shamanaAushadhi: compiledAushadhi || "Classical Ayurvedic Formulations",
      dosageDetails: compiledDose,
      anupan: compiledAnupan,
      sevanKala: compiledKala,
    };

    try {
      if (editingChikitsa) {
        await updateChikitsaProtocol({ id: editingChikitsa.id, ...payload }).unwrap();
        toast.success("Chikitsa protocol updated!");
      } else {
        await createChikitsaProtocol(payload).unwrap();
        toast.success("New Chikitsa protocol created!");
      }
      setIsChikitsaDialogOpen(false);
      refetchChikitsa();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save Chikitsa protocol");
    }
  };

  const handleDeleteChikitsa = async (id: string, name: string) => {
    if (!window.confirm(`Delete Chikitsa protocol "${name}"?`)) return;
    try {
      await deleteChikitsaProtocol(id).unwrap();
      toast.success("Chikitsa protocol removed");
      refetchChikitsa();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete");
    }
  };

  // =========================================================================
  // 5. REVIEW SUBMISSION
  // =========================================================================
  const handleReviewSubmission = async (id: string, reviewStatus: "APPROVED" | "REJECTED") => {
    try {
      await reviewSubmission({
        id,
        reviewStatus,
        adminNotes: reviewStatus === "APPROVED" ? "Approved by Admin" : "Declined by Admin",
        convertToResource: reviewStatus === "APPROVED",
      }).unwrap();
      toast.success(`Submission marked as ${reviewStatus}${reviewStatus === "APPROVED" ? " & added to Drive Vaults!" : ""}`);
      refetchSubs();
      refetchDrive();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to review submission");
    }
  };

  const driveResources: ExploreDriveResource[] = driveData?.data || [];
  const dictionaryTerms: ExploreDictionaryTerm[] = dictData?.data || [];
  const dravyaHerbs: ExploreDravyagunaHerb[] = dravyaData?.data || [];
  const chikitsaProtocols: ExploreChikitsaProtocol[] = chikitsaData?.data || [];
  const pendingSubmissions: ExploreSubmission[] = subData?.data || [];

  // Sub-Navigation Pills Component to cleanly hop between separate sections
  const renderSubNavPills = () => (
    <div className="flex flex-wrap items-center gap-2 pb-2">
      <Button
        variant={currentSection === "overview" ? "default" : "outline"}
        size="sm"
        onClick={() => handleNavigateSection("overview")}
        className="h-8 gap-1.5 text-xs font-bold"
      >
        <Compass className="h-3.5 w-3.5" /> Hub Overview
      </Button>
      <Button
        variant={currentSection === "drive" ? "default" : "outline"}
        size="sm"
        onClick={() => handleNavigateSection("drive")}
        className="h-8 gap-1.5 text-xs font-bold"
      >
        <FolderOpen className="h-3.5 w-3.5 text-sky-500" /> Drive Vaults ({driveResources.length})
      </Button>
      <Button
        variant={currentSection === "dictionary" ? "default" : "outline"}
        size="sm"
        onClick={() => handleNavigateSection("dictionary")}
        className="h-8 gap-1.5 text-xs font-bold"
      >
        <BookOpen className="h-3.5 w-3.5 text-purple-500" /> Dictionary ({dictionaryTerms.length})
      </Button>
      <Button
        variant={currentSection === "dravya" ? "default" : "outline"}
        size="sm"
        onClick={() => handleNavigateSection("dravya")}
        className="h-8 gap-1.5 text-xs font-bold"
      >
        <Leaf className="h-3.5 w-3.5 text-emerald-500" /> Dravya Herbology ({dravyaHerbs.length})
      </Button>
      <Button
        variant={currentSection === "chikitsa" ? "default" : "outline"}
        size="sm"
        onClick={() => handleNavigateSection("chikitsa")}
        className="h-8 gap-1.5 text-xs font-bold"
      >
        <Activity className="h-3.5 w-3.5 text-rose-500" /> Chikitsa Protocols ({chikitsaProtocols.length})
      </Button>
      <Button
        variant={currentSection === "submissions" ? "default" : "outline"}
        size="sm"
        onClick={() => handleNavigateSection("submissions")}
        className="h-8 gap-1.5 text-xs font-bold"
      >
        <FileCheck2 className="h-3.5 w-3.5 text-amber-500" /> Submissions ({pendingSubmissions.length})
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      {/* =====================================================================
          1. SECTION: OVERVIEW HUB (When on /explore)
         ===================================================================== */}
      {currentSection === "overview" && (
        <div className="space-y-6">
          <PageHeader
            title="Explore Knowledge Hub Overview"
            description="Centralized administration for BAMS Atlases, Classical Dictionary, Dravyaguna Materia Medica & Chikitsa Protocols"
            action={
              <Button
                onClick={handleOpenAddCategory}
                className="gap-2 font-bold shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus className="h-4 w-4" /> + Create New Main Category
              </Button>
            }
          />

          {renderSubNavPills()}

          {/* 4 Separate Domain Portal Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Card 1: Drive Vaults */}
            <div className="relative flex flex-col justify-between rounded-2xl border bg-card p-6 shadow-sm hover:shadow-md hover:border-sky-500/50 transition-all">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                    <FolderOpen className="h-6 w-6" />
                  </span>
                  <Badge variant="outline" className="font-bold text-xs bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-300">
                    {driveResources.length} Vaults
                  </Badge>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Drive Resources & Books</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Google Drive folders, anatomy dissection atlases, university question banks, and notes.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t flex items-center justify-between">
                <Button
                  size="sm"
                  onClick={handleOpenAddDrive}
                  variant="outline"
                  className="h-8 text-xs font-bold gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Vault
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleNavigateSection("drive")}
                  className="h-8 text-xs font-bold gap-1 bg-sky-600 hover:bg-sky-700 text-white"
                >
                  Open Section <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Card 2: Classical Dictionary */}
            <div className="relative flex flex-col justify-between rounded-2xl border bg-card p-6 shadow-sm hover:shadow-md hover:border-purple-500/50 transition-all">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                    <BookOpen className="h-6 w-6" />
                  </span>
                  <Badge variant="outline" className="font-bold text-xs bg-purple-50 text-purple-800 dark:bg-purple-950 dark:text-purple-300">
                    {dictionaryTerms.length} Terms
                  </Badge>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Classical Dictionary</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sanskrit terminology, Dosha/Dhatu definitions, Samhita references, and shlokas.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t flex items-center justify-between">
                <Button
                  size="sm"
                  onClick={handleOpenAddDict}
                  variant="outline"
                  className="h-8 text-xs font-bold gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Term
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleNavigateSection("dictionary")}
                  className="h-8 text-xs font-bold gap-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Open Section <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Card 3: Dravya Herbology */}
            <div className="relative flex flex-col justify-between rounded-2xl border bg-card p-6 shadow-sm hover:shadow-md hover:border-emerald-500/50 transition-all">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                    <Leaf className="h-6 w-6" />
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="font-bold text-xs bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      {dravyaHerbs.length} Plants
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-800 text-[10px]">5 Free</Badge>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Dravya Herbology</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Rasa Panchaka, botanical classification, morphology, and therapeutic uses.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t flex items-center justify-between">
                <Button
                  size="sm"
                  onClick={handleOpenAddDravya}
                  variant="outline"
                  className="h-8 text-xs font-bold gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Plant
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleNavigateSection("dravya")}
                  className="h-8 text-xs font-bold gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Open Section <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Card 4: Chikitsa Protocols */}
            <div className="relative flex flex-col justify-between rounded-2xl border bg-card p-6 shadow-sm hover:shadow-md hover:border-rose-500/50 transition-all">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                    <Activity className="h-6 w-6" />
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="font-bold text-xs bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-300">
                      {chikitsaProtocols.length} Protocols
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-800 text-[10px]">5 Free</Badge>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Chikitsa Protocols</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Classical treatment guidelines, Chikitsa Sutras, Shaman Aushadhi, and Anupan.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t flex items-center justify-between">
                <Button
                  size="sm"
                  onClick={handleOpenAddChikitsa}
                  variant="outline"
                  className="h-8 text-xs font-bold gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Protocol
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleNavigateSection("chikitsa")}
                  className="h-8 text-xs font-bold gap-1 bg-rose-600 hover:bg-rose-700 text-white"
                >
                  Open Section <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Dynamic Categories Ribbon */}
          <div className="bg-card p-5 rounded-2xl border shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-bold text-foreground uppercase tracking-wider">
                    Configured Main Categories ({categories.length})
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These categories appear as top-level knowledge sections in the mobile app.
                </p>
              </div>
              <Button
                size="sm"
                onClick={handleOpenAddCategory}
                variant="outline"
                className="gap-1.5 text-xs font-bold"
              >
                <Plus className="h-3.5 w-3.5" /> + Add Another Category
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  onClick={() => {
                    if (cat.categoryType === "DRIVE") handleNavigateSection("drive");
                    else if (cat.categoryType === "DICTIONARY") handleNavigateSection("dictionary");
                    else if (cat.categoryType === "DRAVYA") handleNavigateSection("dravya");
                    else if (cat.categoryType === "CHIKITSA") handleNavigateSection("chikitsa");
                  }}
                  className="cursor-pointer flex items-center justify-between p-3.5 rounded-xl border bg-background/60 hover:bg-muted/40 hover:border-primary/50 transition-all shadow-xs"
                >
                  <div className="flex items-center gap-2.5 overflow-hidden">
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-bold text-xs"
                      style={{ backgroundColor: cat.bgColor || "#ECFDF5", color: cat.color || "#059669" }}
                    >
                      {cat.title.charAt(0)}
                    </span>
                    <div className="truncate">
                      <h5 className="text-xs font-bold text-foreground truncate">{cat.title}</h5>
                      <p className="text-[10px] text-muted-foreground">
                        {cat.categoryType} • {cat.itemCount || 0} items
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditCategory(cat);
                      }}
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(cat.id, cat.title);
                      }}
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          2. DEDICATED SECTION: DRIVE RESOURCES & BOOKS (/explore/drive)
         ===================================================================== */}
      {currentSection === "drive" && (
        <div className="space-y-5">
          <PageHeader
            title="Drive Resources & Books Vault"
            description="Manage Google Drive cloud folders, high-res dissection atlases, and BAMS handwritten notes"
            action={
              <Button onClick={handleOpenAddDrive} className="gap-2 font-bold shadow-sm bg-sky-600 hover:bg-sky-700 text-white">
                <Plus className="h-4 w-4" /> + Add Drive Resource
              </Button>
            }
          />

          {renderSubNavPills()}

          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-card p-4 rounded-xl border">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search vaults by title or subject..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Label className="text-xs font-bold text-muted-foreground uppercase">Academic Year:</Label>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="w-40 bg-background">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  <SelectItem value="1st">1st Year BAMS</SelectItem>
                  <SelectItem value="2nd">2nd Year BAMS</SelectItem>
                  <SelectItem value="3rd">3rd Year BAMS</SelectItem>
                  <SelectItem value="4th">4th Year BAMS</SelectItem>
                  <SelectItem value="reference">Reference / PG</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loadingDrive ? (
            <div className="p-12 text-center text-muted-foreground">Loading Drive resources...</div>
          ) : driveResources.length === 0 ? (
            <div className="p-12 text-center border rounded-2xl bg-card/40">
              <Compass className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-foreground">No Drive Resources Found</h3>
              <p className="text-sm text-muted-foreground mb-4">Click below to add your first Google Drive folder, PDF vault, or Atlas.</p>
              <Button onClick={handleOpenAddDrive} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" /> Add Drive Link
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {driveResources.map((item) => (
                <div
                  key={item.id}
                  className="group relative flex flex-col justify-between rounded-2xl border bg-card p-5 shadow-sm transition-all hover:shadow-md hover:border-sky-500/50"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                          <FolderOpen className="h-5 w-5" />
                        </span>
                        <div>
                          <Badge variant="outline" className="text-[10px] uppercase font-bold tracking-wider">
                            {item.yearKey.toUpperCase()} YEAR
                          </Badge>
                          {item.isPopular && (
                            <Badge className="ml-1.5 bg-amber-500/10 text-amber-600 border-amber-500/20 text-[10px]">
                              Popular
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEditDrive(item)}
                          className="h-8 w-8 text-muted-foreground hover:text-primary"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteDrive(item.id, item.title)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold text-foreground text-base leading-snug">{item.title}</h4>
                      {item.tagLine && (
                        <p className="text-xs font-medium text-sky-600 dark:text-sky-400 mt-0.5">{item.tagLine}</p>
                      )}
                      <p className="text-xs font-semibold text-muted-foreground mt-1">{item.subjectName}</p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{item.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-medium">
                      {item.resourceType} • {item.fileCountOrSize}
                    </span>
                    <a
                      href={item.driveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:underline"
                    >
                      Open Link <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =====================================================================
          3. DEDICATED SECTION: CLASSICAL DICTIONARY (/explore/dictionary)
         ===================================================================== */}
      {currentSection === "dictionary" && (
        <div className="space-y-5">
          <PageHeader
            title="Classical Ayurvedic Dictionary"
            description="Manage Sanskrit Ayurvedic terms, Dosha/Dhatu concepts, definitions, classical references, and shlokas"
            action={
              <Button onClick={handleOpenAddDict} className="gap-2 font-bold shadow-sm bg-purple-600 hover:bg-purple-700 text-white">
                <Plus className="h-4 w-4" /> + Add Dictionary Term
              </Button>
            }
          />

          {renderSubNavPills()}

          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-card p-4 rounded-xl border">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search Sanskrit term or English definition..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Label className="text-xs font-bold text-muted-foreground uppercase">Category:</Label>
              <Select value={dictCategoryFilter} onValueChange={setDictCategoryFilter}>
                <SelectTrigger className="w-44 bg-background">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Dosha">Dosha</SelectItem>
                  <SelectItem value="Dhatu">Dhatu & Upadhatu</SelectItem>
                  <SelectItem value="Mala">Mala</SelectItem>
                  <SelectItem value="Sharira Rachana">Sharira Rachana (Anatomy)</SelectItem>
                  <SelectItem value="Sharira Kriya">Sharira Kriya (Physiology)</SelectItem>
                  <SelectItem value="Dravyaguna">Dravyaguna (Pharmacology)</SelectItem>
                  <SelectItem value="General">General Classical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Alphabet Letters Filter Bar */}
          <div className="flex items-center gap-1.5 overflow-x-auto py-1">
            {["ALL", "A", "B", "C", "D", "G", "H", "J", "K", "M", "N", "O", "P", "R", "S", "T", "V"].map((letter) => (
              <Button
                key={letter}
                size="sm"
                variant={dictLetterFilter === letter ? "default" : "outline"}
                onClick={() => setDictLetterFilter(letter)}
                className="h-7 px-2.5 text-xs font-bold shrink-0"
              >
                {letter}
              </Button>
            ))}
          </div>

          {loadingDict ? (
            <div className="p-12 text-center text-muted-foreground">Loading Dictionary terms...</div>
          ) : dictionaryTerms.length === 0 ? (
            <div className="p-12 text-center border rounded-2xl bg-card/40">
              <BookOpen className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-foreground">No Dictionary Terms Found</h3>
              <p className="text-sm text-muted-foreground mb-4">Click below to add classical Ayurvedic terminology and definitions.</p>
              <Button onClick={handleOpenAddDict} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" /> Add Term
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dictionaryTerms.map((term) => (
                <div
                  key={term.id}
                  className="rounded-2xl border bg-card p-5 shadow-sm transition-all hover:shadow-md hover:border-purple-500/50 flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-purple-600 bg-purple-50 dark:bg-purple-950 dark:text-purple-300 px-2 py-0.5 rounded-md">
                          {term.category}
                        </span>
                        <h4 className="font-bold text-lg text-foreground mt-2">{term.termSanskrit}</h4>
                        <p className="text-sm font-semibold text-muted-foreground">{term.termEnglish}</p>
                        {term.phonetic && <p className="text-xs text-muted-foreground italic">({term.phonetic})</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEditDict(term)}
                          className="h-8 w-8 text-muted-foreground hover:text-primary"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteDict(term.id, term.termSanskrit)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <p className="text-xs text-foreground/80 line-clamp-3">{term.definition}</p>

                    {term.shloka && (
                      <div className="bg-purple-50/50 dark:bg-purple-950/40 p-2.5 rounded-lg border border-purple-100 dark:border-purple-900 text-xs font-mono text-purple-900 dark:text-purple-200">
                        {term.shloka}
                      </div>
                    )}
                  </div>

                  {term.classicalReference && (
                    <div className="mt-3 pt-3 border-t text-[11px] text-muted-foreground font-medium flex items-center justify-between">
                      <span>Ref: {term.classicalReference}</span>
                      <Badge variant="outline" className="text-[10px]">
                        Letter: {term.firstLetter}
                      </Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =====================================================================
          4. DEDICATED SECTION: DRAVYA HERBOLOGY (/explore/dravya)
         ===================================================================== */}
      {currentSection === "dravya" && (
        <div className="space-y-5">
          <PageHeader
            title="Dravya Herbology (Materia Medica)"
            description="Clinical botanical monographs, Rasa Panchaka & Karma, Morphology taxonomy, and therapeutic formulations"
            action={
              <Button onClick={handleOpenAddDravya} className="gap-2 font-bold shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                <Plus className="h-4 w-4" /> + Add Medicinal Plant (Dravya)
              </Button>
            }
          />

          {renderSubNavPills()}

          {/* Freemium Notification */}
          <div className="flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-900 px-4 py-2.5 rounded-xl">
            <Sparkles className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              <strong>Freemium Gating Status:</strong> Entries #1–#5 are free for all students. Entries #6+ require an active paid subscription to unlock full clinical formulations.
            </span>
          </div>

          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-card p-4 rounded-xl border">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search plant, botanical name, family, rasa, karma..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
          </div>

          {loadingDravya ? (
            <div className="p-12 text-center text-muted-foreground">Loading Dravyaguna herbs...</div>
          ) : dravyaHerbs.length === 0 ? (
            <div className="p-12 text-center border rounded-2xl bg-card/40">
              <Leaf className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-foreground">No Medicinal Plants Found</h3>
              <p className="text-sm text-muted-foreground mb-4">Add your first herb with Rasa Panchaka, Morphology & Clinical Uses.</p>
              <Button onClick={handleOpenAddDravya} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" /> Add Herb
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {dravyaHerbs.map((herb, idx) => {
                const isFreeTier = idx < 5;
                return (
                  <div
                    key={herb.id}
                    className="group relative flex flex-col justify-between rounded-2xl border bg-card p-5 shadow-sm transition-all hover:shadow-md hover:border-emerald-500/50"
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {herb.imageUrl ? (
                            <img
                              src={herb.imageUrl}
                              alt={herb.sanskritName}
                              className="h-12 w-12 rounded-xl object-cover border"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-xl bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-900 flex items-center justify-center text-emerald-700 dark:text-emerald-300">
                              <Leaf className="h-6 w-6" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="font-bold text-base text-foreground">{herb.sanskritName}</h4>
                              <Badge
                                variant={isFreeTier ? "secondary" : "outline"}
                                className={`text-[10px] ${
                                  isFreeTier
                                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                                    : "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300"
                                }`}
                              >
                                {isFreeTier ? (
                                  <span className="flex items-center gap-1">
                                    <Unlock className="h-2.5 w-2.5" /> FREE #{idx + 1}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <Lock className="h-2.5 w-2.5" /> PREMIUM #{idx + 1}
                                  </span>
                                )}
                              </Badge>
                            </div>
                            <p className="text-xs italic text-muted-foreground font-medium">{herb.botanicalName}</p>
                            <p className="text-[11px] text-muted-foreground">{herb.family}</p>
                          </div>
                        </div>

                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEditDravya(herb)}
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteDravya(herb.id, herb.sanskritName)}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {herb.pradhanKarma && (
                        <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/40 px-2.5 py-1 rounded-md">
                          Primary Action: {herb.pradhanKarma}
                        </div>
                      )}

                      {/* Rasa Panchaka Summary */}
                      <div className="grid grid-cols-2 gap-2 text-[11px] bg-muted/40 p-2.5 rounded-xl border">
                        <div>
                          <span className="font-semibold text-muted-foreground">Rasa:</span> {herb.rasa}
                        </div>
                        <div>
                          <span className="font-semibold text-muted-foreground">Virya:</span> {herb.virya}
                        </div>
                        <div>
                          <span className="font-semibold text-muted-foreground">Vipaka:</span> {herb.vipaka}
                        </div>
                        <div>
                          <span className="font-semibold text-muted-foreground">Part:</span> {herb.partUsed}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-foreground/80 line-clamp-2">
                          <strong>Indications:</strong> {herb.majorIndications}
                        </p>
                        {herb.vishishtYoga && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            <strong>Formulations:</strong> {herb.vishishtYoga}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t text-[11px] text-muted-foreground flex items-center justify-between">
                      <span>Dosage: {herb.classicalDosage}</span>
                      {herb.isPopular && <Badge className="bg-amber-500 text-[10px]">Popular Herb</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* =====================================================================
          5. DEDICATED SECTION: CHIKITSA PROTOCOLS (/explore/chikitsa)
         ===================================================================== */}
      {currentSection === "chikitsa" && (
        <div className="space-y-5">
          <PageHeader
            title="Chikitsa Protocols (Clinical Guidelines)"
            description="Standardized Kayachikitsa treatment protocols with Chikitsa Sutras, Shaman Aushadhi, Anupan, and modern correlation"
            action={
              <Button onClick={handleOpenAddChikitsa} className="gap-2 font-bold shadow-sm bg-rose-600 hover:bg-rose-700 text-white">
                <Plus className="h-4 w-4" /> + Add Chikitsa Protocol
              </Button>
            }
          />

          {renderSubNavPills()}

          {/* Freemium Notification */}
          <div className="flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-900 px-4 py-2.5 rounded-xl">
            <Sparkles className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              <strong>Freemium Gating Status:</strong> Entries #1–#5 are free for all students. Entries #6+ require an active paid subscription to unlock full treatment protocols.
            </span>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-card p-4 rounded-xl border">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search disease, dosha, sutra, shamana..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Label className="text-xs font-bold text-muted-foreground uppercase">Dosha:</Label>
              <Select value={doshaFilter} onValueChange={setDoshaFilter}>
                <SelectTrigger className="w-40 bg-background">
                  <SelectValue placeholder="All Doshas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doshas</SelectItem>
                  <SelectItem value="Vata">Vata</SelectItem>
                  <SelectItem value="Pitta">Pitta</SelectItem>
                  <SelectItem value="Kapha">Kapha</SelectItem>
                  <SelectItem value="Vata-Pitta">Vata-Pitta</SelectItem>
                  <SelectItem value="Vata-Kapha">Vata-Kapha</SelectItem>
                  <SelectItem value="Pitta-Kapha">Pitta-Kapha</SelectItem>
                  <SelectItem value="Tridosha">Tridoshaja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loadingChikitsa ? (
            <div className="p-12 text-center text-muted-foreground">Loading Chikitsa protocols...</div>
          ) : chikitsaProtocols.length === 0 ? (
            <div className="p-12 text-center border rounded-2xl bg-card/40">
              <Activity className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-foreground">No Chikitsa Protocols Found</h3>
              <p className="text-sm text-muted-foreground mb-4">Add disease management protocols with classical sutras & shamana aushadhi.</p>
              <Button onClick={handleOpenAddChikitsa} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" /> Add Protocol
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {chikitsaProtocols.map((proto, idx) => {
                const isFreeTier = idx < 5;
                return (
                  <div
                    key={proto.id}
                    className="group relative flex flex-col justify-between rounded-2xl border bg-card p-5 shadow-sm transition-all hover:shadow-md hover:border-rose-500/50"
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <h4 className="font-bold text-base text-foreground">{proto.diseaseNameSanskrit}</h4>
                            <Badge
                              variant={isFreeTier ? "secondary" : "outline"}
                              className={`text-[10px] ${
                                isFreeTier
                                  ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                                  : "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300"
                              }`}
                            >
                              {isFreeTier ? (
                                <span className="flex items-center gap-1">
                                  <Unlock className="h-2.5 w-2.5" /> FREE #{idx + 1}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <Lock className="h-2.5 w-2.5" /> PREMIUM #{idx + 1}
                                </span>
                              )}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground font-semibold">{proto.diseaseNameEnglish}</p>
                          <Badge variant="outline" className="mt-1 text-[10px] text-rose-700 bg-rose-50 border-rose-200 dark:bg-rose-950 dark:text-rose-300">
                            Dosha: {proto.doshaPredominance}
                          </Badge>
                        </div>

                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEditChikitsa(proto)}
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteChikitsa(proto.id, proto.diseaseNameSanskrit)}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Chikitsa Sutra */}
                      <div className="bg-rose-50/60 dark:bg-rose-950/40 p-2.5 rounded-xl border border-rose-100 dark:border-rose-900">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300 block mb-1">
                          Chikitsa Sutra:
                        </span>
                        <p className="text-xs font-mono text-rose-950 dark:text-rose-200 italic line-clamp-2">
                          "{proto.chikitsaSutra}"
                        </p>
                      </div>

                      {/* Shamana Aushadhi & Anupan */}
                      <div className="space-y-1 text-xs">
                        <p className="text-foreground/90 line-clamp-2">
                          <strong>Shamana Aushadhi:</strong> {proto.shamanaAushadhi}
                        </p>
                        {proto.anupan && (
                          <p className="text-muted-foreground text-[11px]">
                            <strong>Anupan:</strong> {proto.anupan}
                          </p>
                        )}
                        {proto.modernTreatment && (
                          <p className="text-muted-foreground text-[11px] line-clamp-2">
                            <strong>Modern:</strong> {proto.modernTreatment}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t text-[11px] text-muted-foreground flex items-center justify-between">
                      <span>Ref: {proto.referenceSamhita}</span>
                      {proto.isPopular && <Badge className="bg-amber-500 text-[10px]">High Yield</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* =====================================================================
          6. DEDICATED SECTION: SUBMISSIONS QUEUE (/explore/submissions)
         ===================================================================== */}
      {currentSection === "submissions" && (
        <div className="space-y-5">
          <PageHeader
            title="Student Community Submissions"
            description="Review Google Drive links, question banks, and notes contributed by BAMS students with 1-click approval"
          />

          {renderSubNavPills()}

          {/* Filter Bar */}
          <div className="flex items-center gap-3 bg-card p-4 rounded-xl border">
            <Label className="text-xs font-bold text-muted-foreground uppercase">Review Status:</Label>
            <Select value={submissionStatusFilter} onValueChange={setSubmissionStatusFilter}>
              <SelectTrigger className="w-44 bg-background">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Submissions</SelectItem>
                <SelectItem value="PENDING">Pending Review</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="REJECTED">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loadingSubs ? (
            <div className="p-12 text-center text-muted-foreground">Loading submissions...</div>
          ) : pendingSubmissions.length === 0 ? (
            <div className="p-12 text-center border rounded-2xl bg-card/40">
              <CheckCircle2 className="h-10 w-10 text-emerald-500/50 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-foreground">No Submissions Found</h3>
              <p className="text-sm text-muted-foreground">When students submit notes or Drive links from the mobile app, they will appear here for review.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingSubmissions.map((sub) => (
                <div
                  key={sub.id}
                  className="rounded-2xl border bg-card p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
                >
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] font-bold uppercase">
                        {sub.resourceType}
                      </Badge>
                      <Badge className={
                        sub.reviewStatus === "APPROVED"
                          ? "bg-emerald-100 text-emerald-800 text-[10px]"
                          : sub.reviewStatus === "REJECTED"
                          ? "bg-rose-100 text-rose-800 text-[10px]"
                          : "bg-amber-100 text-amber-800 text-[10px]"
                      }>
                        {sub.reviewStatus}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-medium">Year: {sub.academicYear}</span>
                    </div>

                    <h4 className="font-bold text-base text-foreground">{sub.title}</h4>
                    <p className="text-xs text-muted-foreground font-medium">
                      Subject: {sub.subjectName} • Submitted by: <strong>{sub.userName}</strong> ({sub.userEmail})
                    </p>

                    {sub.notesRemarks && (
                      <p className="text-xs text-foreground/80 mt-1 italic bg-muted/30 p-2 rounded-lg">
                        "{sub.notesRemarks}"
                      </p>
                    )}

                    <div className="pt-1">
                      <a
                        href={sub.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:underline"
                      >
                        Inspect Resource Link <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>

                  {sub.reviewStatus === "PENDING" && (
                    <div className="flex items-center gap-2 self-end md:self-center">
                      <Button
                        size="sm"
                        onClick={() => handleReviewSubmission(sub.id, "APPROVED")}
                        className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 font-semibold text-white"
                      >
                        <CheckCircle2 className="h-4 w-4" /> Approve & Add to Vault
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReviewSubmission(sub.id, "REJECTED")}
                        className="gap-1.5 text-destructive hover:bg-destructive/10"
                      >
                        <XCircle className="h-4 w-4" /> Decline
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =========================================================================
          DIALOG 0: DYNAMIC EXPLORE CATEGORY MODAL
         ========================================================================= */}
      <Dialog open={isCatDialogOpen} onOpenChange={setIsCatDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Explore Category" : "Create Dynamic Explore Category"}</DialogTitle>
            <DialogDescription>
              Add multiple custom knowledge categories with custom schemas, descriptions, and freemium rules.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCategory} className="space-y-4 pt-2">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Category Title *</Label>
                <Input
                  required
                  placeholder="e.g. Research Papers & Clinical Studies"
                  value={catForm.title}
                  onChange={(e) => setCatForm({ ...catForm, title: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Tag Line / Subtitle</Label>
                <Input
                  placeholder="e.g. Peer-reviewed BAMS case trials and classical journals"
                  value={catForm.tagLine}
                  onChange={(e) => setCatForm({ ...catForm, tagLine: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold">Module Schema / Type *</Label>
                  <Select
                    value={catForm.categoryType}
                    onValueChange={(v: any) => setCatForm({ ...catForm, categoryType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRIVE">Drive / Books Vault</SelectItem>
                      <SelectItem value="DICTIONARY">Classical Dictionary</SelectItem>
                      <SelectItem value="DRAVYA">Dravya (Materia Medica)</SelectItem>
                      <SelectItem value="CHIKITSA">Chikitsa Protocols</SelectItem>
                      <SelectItem value="CUSTOM">Custom Resource</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold">Free Entries Limit</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder="5 (0 for all free)"
                    value={catForm.freeLimit}
                    onChange={(e) => setCatForm({ ...catForm, freeLimit: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold">Badge Text (Optional)</Label>
                  <Input
                    placeholder="e.g. 5 Free or High Yield"
                    value={catForm.badgeText}
                    onChange={(e) => setCatForm({ ...catForm, badgeText: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold">Theme Color</Label>
                  <Input
                    type="text"
                    placeholder="#059669"
                    value={catForm.color}
                    onChange={(e) => setCatForm({ ...catForm, color: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Description</Label>
                <Textarea
                  rows={3}
                  placeholder="Detailed overview of what this knowledge category contains for students..."
                  value={catForm.description}
                  onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Switch
                  checked={catForm.isActive}
                  onCheckedChange={(c) => setCatForm({ ...catForm, isActive: c })}
                  id="cat-active-switch"
                />
                <Label htmlFor="cat-active-switch" className="text-xs font-medium cursor-pointer">
                  Category is Active & Visible in Mobile App
                </Label>
              </div>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsCatDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatingCat || isUpdatingCat} className="font-bold">
                {editingCategory ? "Update Category" : "Create Category"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* =========================================================================
          DIALOG 1: DRIVE RESOURCE MODAL
         ========================================================================= */}
      <Dialog open={isDriveDialogOpen} onOpenChange={setIsDriveDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDrive ? "Edit Drive Resource" : "Add New Drive Vault or Atlas"}</DialogTitle>
            <DialogDescription>
              Configure Google Drive folders, anatomy atlases, and high-yield notes for BAMS students.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveDrive} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold">Resource Title *</Label>
                <Input
                  required
                  placeholder="e.g. BD Chaurasia Dissection Atlas & High-Res Plates"
                  value={driveForm.title}
                  onChange={(e) => setDriveForm({ ...driveForm, title: e.target.value })}
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold">Tag Line / Subtitle</Label>
                <Input
                  placeholder="e.g. Complete Osteology & Viscera Drive Vault with Cadaveric Photos"
                  value={driveForm.tagLine}
                  onChange={(e) => setDriveForm({ ...driveForm, tagLine: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Subject Name *</Label>
                <Input
                  required
                  placeholder="e.g. Rachana Sharir (Anatomy)"
                  value={driveForm.subjectName}
                  onChange={(e) => setDriveForm({ ...driveForm, subjectName: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Academic Year *</Label>
                <Select
                  value={driveForm.yearKey}
                  onValueChange={(v) => {
                    const nameMap: any = {
                      "1st": "1st Year BAMS",
                      "2nd": "2nd Year BAMS",
                      "3rd": "3rd Year BAMS",
                      "4th": "4th Year BAMS",
                      reference: "Reference / PG",
                      all: "All Professional Years",
                    };
                    setDriveForm({ ...driveForm, yearKey: v, yearName: nameMap[v] || v });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1st">1st Year BAMS</SelectItem>
                    <SelectItem value="2nd">2nd Year BAMS</SelectItem>
                    <SelectItem value="3rd">3rd Year BAMS</SelectItem>
                    <SelectItem value="4th">4th Year BAMS</SelectItem>
                    <SelectItem value="reference">Reference / PG</SelectItem>
                    <SelectItem value="all">All Professional Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold">Google Drive or Cloud URL *</Label>
                <Input
                  required
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={driveForm.driveUrl}
                  onChange={(e) => setDriveForm({ ...driveForm, driveUrl: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Resource Type</Label>
                <Select
                  value={driveForm.resourceType}
                  onValueChange={(v) => setDriveForm({ ...driveForm, resourceType: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Google Drive Folder">Google Drive Folder</SelectItem>
                    <SelectItem value="Drive Atlas">Drive Atlas (Anatomy / Dravyaguna)</SelectItem>
                    <SelectItem value="PDF Vault">PDF Vault</SelectItem>
                    <SelectItem value="Drive Notes">Subject Handwritten Notes</SelectItem>
                    <SelectItem value="Question Bank">Question Bank / University Papers</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">File Count / Size Label</Label>
                <Input
                  placeholder="e.g. 24 PDFs • 120 MB"
                  value={driveForm.fileCountOrSize}
                  onChange={(e) => setDriveForm({ ...driveForm, fileCountOrSize: e.target.value })}
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold">Description</Label>
                <Textarea
                  rows={3}
                  placeholder="Curated notes, clinical flowcharts, and color-coded diagrams..."
                  value={driveForm.description}
                  onChange={(e) => setDriveForm({ ...driveForm, description: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-2 sm:col-span-2 pt-2">
                <Switch
                  checked={driveForm.isPopular}
                  onCheckedChange={(c) => setDriveForm({ ...driveForm, isPopular: c })}
                  id="popular-switch"
                />
                <Label htmlFor="popular-switch" className="text-xs font-medium cursor-pointer">
                  Feature as "Popular / High Yield Vault"
                </Label>
              </div>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsDriveDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatingDrive || isUpdatingDrive} className="font-bold">
                {editingDrive ? "Update Vault" : "Create Drive Vault"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* =========================================================================
          DIALOG 2: DICTIONARY TERM MODAL
         ========================================================================= */}
      <Dialog open={isDictDialogOpen} onOpenChange={setIsDictDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDict ? "Edit Dictionary Term" : "Add Classical Ayurvedic Term"}</DialogTitle>
            <DialogDescription>
              Define Sanskrit Ayurvedic terms, Dosha/Dhatu concepts, and classical shloka references.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveDict} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Sanskrit Term (Devanagari) *</Label>
                <Input
                  required
                  placeholder="e.g. ओजस् (Ojas)"
                  value={dictForm.termSanskrit}
                  onChange={(e) => setDictForm({ ...dictForm, termSanskrit: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">English / Transliteration *</Label>
                <Input
                  required
                  placeholder="e.g. Ojas / Supreme Vital Essence"
                  value={dictForm.termEnglish}
                  onChange={(e) => setDictForm({ ...dictForm, termEnglish: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Category</Label>
                <Select
                  value={dictForm.category}
                  onValueChange={(v) => setDictForm({ ...dictForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dosha">Dosha</SelectItem>
                    <SelectItem value="Dhatu">Dhatu & Upadhatu</SelectItem>
                    <SelectItem value="Mala">Mala</SelectItem>
                    <SelectItem value="Sharira Rachana">Sharira Rachana (Anatomy)</SelectItem>
                    <SelectItem value="Sharira Kriya">Sharira Kriya (Physiology)</SelectItem>
                    <SelectItem value="Dravyaguna">Dravyaguna (Pharmacology)</SelectItem>
                    <SelectItem value="General">General Classical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Classical Reference</Label>
                <Input
                  placeholder="e.g. Charaka Samhita Sutrasthana 17/74"
                  value={dictForm.classicalReference}
                  onChange={(e) => setDictForm({ ...dictForm, classicalReference: e.target.value })}
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold">Classical Shloka (Optional)</Label>
                <Input
                  placeholder="e.g. ओजस्तु तेजो धातूनां शुक्रान्तानां परं स्मृतम् ।"
                  value={dictForm.shloka}
                  onChange={(e) => setDictForm({ ...dictForm, shloka: e.target.value })}
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold">Comprehensive Definition *</Label>
                <Textarea
                  required
                  rows={4}
                  placeholder="The supreme essence of all sapta dhatus representing vitality, immunity (Vyadhikshamatva) and vigor..."
                  value={dictForm.definition}
                  onChange={(e) => setDictForm({ ...dictForm, definition: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsDictDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatingDict || isUpdatingDict} className="font-bold bg-purple-600 hover:bg-purple-700 text-white">
                {editingDict ? "Update Term" : "Save Dictionary Term"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* =========================================================================
          DIALOG 3: DRAVYA HERBOLOGY MODAL
         ========================================================================= */}
      <Dialog open={isDravyaDialogOpen} onOpenChange={setIsDravyaDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDravya ? "Edit Dravya Entry" : "Add New Medicinal Plant (Dravya)"}</DialogTitle>
            <DialogDescription>
              Configure Botanical details, Rasa Panchaka & Karma, Morphology, and Multiple Dynamic Amayik Prayog entries.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-2">
            <div className="grid grid-cols-4 w-full border rounded-xl p-1 bg-muted/40 mb-4">
              <Button
                type="button"
                variant={dravyaTab === "basic" ? "default" : "ghost"}
                size="sm"
                onClick={() => setDravyaTab("basic")}
                className="text-xs font-bold"
              >
                1. Basic Info
              </Button>
              <Button
                type="button"
                variant={dravyaTab === "rasa" ? "default" : "ghost"}
                size="sm"
                onClick={() => setDravyaTab("rasa")}
                className="text-xs font-bold"
              >
                2. Rasa & Karma
              </Button>
              <Button
                type="button"
                variant={dravyaTab === "morphology" ? "default" : "ghost"}
                size="sm"
                onClick={() => setDravyaTab("morphology")}
                className="text-xs font-bold"
              >
                3. Morphology
              </Button>
              <Button
                type="button"
                variant={dravyaTab === "therapeutic" ? "default" : "ghost"}
                size="sm"
                onClick={() => setDravyaTab("therapeutic")}
                className="text-xs font-bold"
              >
                4. Therapeutic
              </Button>
            </div>

            <form onSubmit={handleSaveDravya} className="space-y-4">
              {/* SECTION: BASIC INFO */}
              {dravyaTab === "basic" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Plant Name (Sanskrit Title) *</Label>
                    <Input
                      required
                      placeholder="e.g. Guduchi (गिलोय)"
                      value={dravyaForm.sanskritName}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, sanskritName: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Botanical Name *</Label>
                    <Input
                      required
                      placeholder="e.g. Tinospora cordifolia"
                      value={dravyaForm.botanicalName}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, botanicalName: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Family *</Label>
                    <Input
                      required
                      placeholder="e.g. Menispermaceae"
                      value={dravyaForm.family}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, family: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Pradhan Karma (Primary Action)</Label>
                    <Input
                      placeholder="e.g. Rasayana, Jvaraghna, Dipana"
                      value={dravyaForm.pradhanKarma}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, pradhanKarma: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Useful Part (Prayojyanga)</Label>
                    <Input
                      placeholder="e.g. Stem (Kanda)"
                      value={dravyaForm.partUsed}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, partUsed: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Photo / Image URL</Label>
                    <Input
                      placeholder="https://..."
                      value={dravyaForm.imageUrl}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, imageUrl: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Synonyms (Paryaya)</Label>
                    <Input
                      placeholder="e.g. Amrita, Chhinnaruha, Chakralakshana, Vatsadani"
                      value={dravyaForm.synonyms}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, synonyms: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Common Names</Label>
                    <Input
                      placeholder="e.g. Giloy (Hindi), Heart-leaved Moonseed (English)"
                      value={dravyaForm.commonNames}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, commonNames: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* SECTION 1: RASA PANCHAKA & KARMA */}
              {dravyaTab === "rasa" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Rasa (Taste) *</Label>
                    <Input
                      required
                      placeholder="e.g. Tikta, Kashaya"
                      value={dravyaForm.rasa}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, rasa: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Guna (Qualities)</Label>
                    <Input
                      placeholder="e.g. Guru, Snigdha"
                      value={dravyaForm.guna}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, guna: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Virya (Potency) *</Label>
                    <Select
                      value={dravyaForm.virya}
                      onValueChange={(v) => setDravyaForm({ ...dravyaForm, virya: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ushna">Ushna (Hot)</SelectItem>
                        <SelectItem value="Sheeta">Sheeta (Cold)</SelectItem>
                        <SelectItem value="Anushnasheeta">Anushnasheeta (Temperate)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Vipaka (Post-Digestive Taste) *</Label>
                    <Select
                      value={dravyaForm.vipaka}
                      onValueChange={(v) => setDravyaForm({ ...dravyaForm, vipaka: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Madhura">Madhura (Sweet)</SelectItem>
                        <SelectItem value="Amla">Amla (Sour)</SelectItem>
                        <SelectItem value="Katu">Katu (Pungent)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Prabhava (Special Action)</Label>
                    <Input
                      placeholder="e.g. Vishaghna, Medhya"
                      value={dravyaForm.prabhava}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, prabhava: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Dosha Karma</Label>
                    <Input
                      placeholder="e.g. Tridosha Shamaka"
                      value={dravyaForm.doshaKarma}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, doshaKarma: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">General Karma</Label>
                    <Input
                      placeholder="e.g. Dipana, Pachana, Dahaprashamana, Mehaghna"
                      value={dravyaForm.generalKarma}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, generalKarma: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* SECTION 2: MORPHOLOGY */}
              {dravyaTab === "morphology" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Habit</Label>
                    <Input
                      placeholder="e.g. Large deciduous woody climber"
                      value={dravyaForm.habit}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, habit: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Stem</Label>
                    <Input
                      placeholder="e.g. Succulent with rosette lenticels"
                      value={dravyaForm.morphologyStem}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, morphologyStem: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Root</Label>
                    <Input
                      placeholder="e.g. Thread-like aerial roots"
                      value={dravyaForm.morphologyRoot}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, morphologyRoot: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Leaf / Leaves</Label>
                    <Input
                      placeholder="e.g. Cordate (heart-shaped), 7-9 nerved"
                      value={dravyaForm.morphologyLeaf}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, morphologyLeaf: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Flower</Label>
                    <Input
                      placeholder="e.g. Unisexual, small, greenish-yellow"
                      value={dravyaForm.morphologyFlower}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, morphologyFlower: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Inflorescence</Label>
                    <Input
                      placeholder="e.g. Axillary and terminal racemes"
                      value={dravyaForm.inflorescence}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, inflorescence: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Fruit</Label>
                    <Input
                      placeholder="e.g. 1-3 drupes, ovoid, succulent, turning red"
                      value={dravyaForm.morphologyFruit}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, morphologyFruit: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Seed</Label>
                    <Input
                      placeholder="e.g. Curved or crescent-shaped seed"
                      value={dravyaForm.morphologySeed}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, morphologySeed: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* SECTION 3: THERAPEUTIC USES */}
              {dravyaTab === "therapeutic" && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Indications (Roga Prayoga) *</Label>
                    <Input
                      required
                      placeholder="e.g. Jwara (Fever), Prameha (Diabetes), Kushtha, Vatarakta, Pandu"
                      value={dravyaForm.majorIndications}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, majorIndications: e.target.value })}
                    />
                  </div>

                  {/* DYNAMIC AMAYIK PRAYOG BUILDER */}
                  <div className="rounded-xl border p-4 bg-muted/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-bold text-foreground">
                          Amayik Prayog Entries ({prayogEntries.length})
                        </Label>
                        <p className="text-[11px] text-muted-foreground">
                          Add multiple condition formulations dynamically.
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleAddPrayogEntry}
                        className="gap-1 text-xs font-bold text-emerald-600 border-emerald-300"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Another Prayog
                      </Button>
                    </div>

                    <div className="space-y-2.5">
                      {prayogEntries.map((entry, pIdx) => (
                        <div
                          key={entry.id}
                          className="flex flex-col sm:flex-row gap-2 items-center bg-card p-3 rounded-lg border"
                        >
                          <span className="text-xs font-bold text-muted-foreground w-6">#{pIdx + 1}</span>
                          <Input
                            placeholder="Condition (e.g. In Vatarakta)"
                            value={entry.title}
                            onChange={(e) => handleUpdatePrayogEntry(entry.id, "title", e.target.value)}
                            className="w-full sm:w-1/3 text-xs"
                          />
                          <Input
                            placeholder="Formulation & Anupan (e.g. Guduchi kwatha + Guggulu)"
                            value={entry.formulation}
                            onChange={(e) => handleUpdatePrayogEntry(entry.id, "formulation", e.target.value)}
                            className="w-full sm:w-2/3 text-xs"
                          />
                          {prayogEntries.length > 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => handleRemovePrayogEntry(entry.id)}
                              className="h-8 w-8 text-destructive shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold">Classical Dosage</Label>
                      <Input
                        placeholder="e.g. Churna: 3-6 gm; Swarasa: 10-20 ml"
                        value={dravyaForm.classicalDosage}
                        onChange={(e) => setDravyaForm({ ...dravyaForm, classicalDosage: e.target.value })}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold">Phyto-constituents</Label>
                      <Input
                        placeholder="e.g. Giloin, Berberine, Tinosporide"
                        value={dravyaForm.phytoConstituents}
                        onChange={(e) => setDravyaForm({ ...dravyaForm, phytoConstituents: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Vishisht Yoga (Special Formulations)</Label>
                    <Input
                      placeholder="e.g. Amritarishta, Sanshamani Vati, Guduchi Ghrita"
                      value={dravyaForm.vishishtYoga}
                      onChange={(e) => setDravyaForm({ ...dravyaForm, vishishtYoga: e.target.value })}
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Switch
                      checked={dravyaForm.isPopular}
                      onCheckedChange={(c) => setDravyaForm({ ...dravyaForm, isPopular: c })}
                      id="dravya-popular"
                    />
                    <Label htmlFor="dravya-popular" className="text-xs font-medium cursor-pointer">
                      Mark as "High Yield Plant for BAMS Exams"
                    </Label>
                  </div>
                </div>
              )}

              <DialogFooter className="pt-4 border-t flex items-center justify-between">
                <div className="flex gap-2">
                  {dravyaTab !== "basic" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (dravyaTab === "therapeutic") setDravyaTab("morphology");
                        else if (dravyaTab === "morphology") setDravyaTab("rasa");
                        else if (dravyaTab === "rasa") setDravyaTab("basic");
                      }}
                    >
                      Previous Tab
                    </Button>
                  )}
                  {dravyaTab !== "therapeutic" && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        if (dravyaTab === "basic") setDravyaTab("rasa");
                        else if (dravyaTab === "rasa") setDravyaTab("morphology");
                        else if (dravyaTab === "morphology") setDravyaTab("therapeutic");
                      }}
                    >
                      Next Tab
                    </Button>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDravyaDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isCreatingDravya || isUpdatingDravya} className="font-bold bg-emerald-600 hover:bg-emerald-700 text-white">
                    {editingDravya ? "Update Plant Entry" : "Save Plant to Dravya"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* =========================================================================
          DIALOG 4: CHIKITSA PROTOCOLS MODAL
         ========================================================================= */}
      <Dialog open={isChikitsaDialogOpen} onOpenChange={setIsChikitsaDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingChikitsa ? "Edit Chikitsa Protocol" : "Add New Chikitsa Protocol"}</DialogTitle>
            <DialogDescription>
              Define Ayurvedic clinical treatment guidelines, classical sutras, multiple dynamic shaman aushadhi, and modern perspectives.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-2">
            <div className="grid grid-cols-4 w-full border rounded-xl p-1 bg-muted/40 mb-4">
              <Button
                type="button"
                variant={chikitsaTab === "basic" ? "default" : "ghost"}
                size="sm"
                onClick={() => setChikitsaTab("basic")}
                className="text-xs font-bold"
              >
                1. Diagnostic
              </Button>
              <Button
                type="button"
                variant={chikitsaTab === "treatment" ? "default" : "ghost"}
                size="sm"
                onClick={() => setChikitsaTab("treatment")}
                className="text-xs font-bold"
              >
                2. Sutra
              </Button>
              <Button
                type="button"
                variant={chikitsaTab === "shaman" ? "default" : "ghost"}
                size="sm"
                onClick={() => setChikitsaTab("shaman")}
                className="text-xs font-bold"
              >
                3. Aushadhi
              </Button>
              <Button
                type="button"
                variant={chikitsaTab === "modern" ? "default" : "ghost"}
                size="sm"
                onClick={() => setChikitsaTab("modern")}
                className="text-xs font-bold"
              >
                4. Modern
              </Button>
            </div>

            <form onSubmit={handleSaveChikitsa} className="space-y-4">
              {/* TAB 1: DIAGNOSTIC INFO */}
              {chikitsaTab === "basic" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Disease Name (Sanskrit Title) *</Label>
                    <Input
                      required
                      placeholder="e.g. Jwara Chikitsa (ज्वर चिकित्सा)"
                      value={chikitsaForm.diseaseNameSanskrit}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, diseaseNameSanskrit: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">English / Modern Equivalent</Label>
                    <Input
                      placeholder="e.g. Pyrexia / Fever Protocols"
                      value={chikitsaForm.diseaseNameEnglish}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, diseaseNameEnglish: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Dosha Predominance</Label>
                    <Input
                      placeholder="e.g. Vata-Pitta / Tridoshaja"
                      value={chikitsaForm.doshaPredominance}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, doshaPredominance: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Dushya (Affected Tissues)</Label>
                    <Input
                      placeholder="e.g. Rasa, Rakta, Sweda"
                      value={chikitsaForm.dushya}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, dushya: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Diagram / Illustration URL (Optional)</Label>
                    <Input
                      placeholder="https://..."
                      value={chikitsaForm.diagramUrl}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, diagramUrl: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Parichay (Introduction)</Label>
                    <Textarea
                      rows={2}
                      placeholder="Brief introduction of the disease pathology..."
                      value={chikitsaForm.parichay}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, parichay: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Paribhasha (Classical Definition)</Label>
                    <Input
                      placeholder="e.g. देहेन्द्रियमनस्तापी सर्वकायप्रभावनः ।"
                      value={chikitsaForm.paribhasha}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, paribhasha: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Samprapti Summary</Label>
                    <Input
                      placeholder="e.g. Mithya Ahara/Vihara -> Agnimandya -> Ama formation"
                      value={chikitsaForm.sampraptiSummary}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, sampraptiSummary: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* TAB 2: CHIKITSA SUTRA */}
              {chikitsaTab === "treatment" && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Chikitsa Sutra (Primary Principle) *</Label>
                    <Textarea
                      required
                      rows={3}
                      placeholder="e.g. ज्वरादौ लङ्घनं प्रोक्तं ज्वरमध्ये तु पाचनम्..."
                      value={chikitsaForm.chikitsaSutra}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, chikitsaSutra: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Vishesh Chikitsa (Special Treatment / Panchakarma)</Label>
                    <Textarea
                      rows={3}
                      placeholder="e.g. In chronic fever: Tikta Sarpi; In Kapha dominance: Vamana"
                      value={chikitsaForm.visheshChikitsa}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, visheshChikitsa: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Shodhana Therapy</Label>
                    <Input
                      placeholder="e.g. Mridu Virechana with Aragvadha Phalamajja"
                      value={chikitsaForm.shodhanaTherapy}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, shodhanaTherapy: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* TAB 3: SHAMAN AUSHADHI */}
              {chikitsaTab === "shaman" && (
                <div className="space-y-4">
                  <div className="rounded-xl border p-4 bg-muted/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-bold text-foreground">
                          Shaman Aushadhi Formulations ({aushadhiList.length})
                        </Label>
                        <p className="text-[11px] text-muted-foreground">
                          Add multiple classical medicines with dosage, anupan, and timing.
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleAddAushadhi}
                        className="gap-1 text-xs font-bold text-rose-600 border-rose-300"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Medicine
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {aushadhiList.map((item, aIdx) => (
                        <div key={item.id} className="p-3 bg-card rounded-xl border space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-muted-foreground">Medicine #{aIdx + 1}</span>
                            {aushadhiList.length > 1 && (
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => handleRemoveAushadhi(item.id)}
                                className="h-7 w-7 text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Input
                              placeholder="Medicine Name (e.g. Tribhuvan Kirti Rasa)"
                              value={item.name}
                              onChange={(e) => handleUpdateAushadhi(item.id, "name", e.target.value)}
                              className="text-xs"
                            />
                            <Input
                              placeholder="Dosage (e.g. 250 mg twice daily)"
                              value={item.dose}
                              onChange={(e) => handleUpdateAushadhi(item.id, "dose", e.target.value)}
                              className="text-xs"
                            />
                            <Input
                              placeholder="Anupan (e.g. Warm water / Honey)"
                              value={item.anupan}
                              onChange={(e) => handleUpdateAushadhi(item.id, "anupan", e.target.value)}
                              className="text-xs"
                            />
                            <Input
                              placeholder="Sevan Kala (e.g. Pragbhakta / Before food)"
                              value={item.kala}
                              onChange={(e) => handleUpdateAushadhi(item.id, "kala", e.target.value)}
                              className="text-xs"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 4: MODERN & DIET */}
              {chikitsaTab === "modern" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Modern Treatment Approaches</Label>
                    <Textarea
                      rows={3}
                      placeholder="e.g. Differential diagnosis rule-out, antipyretics, hydration..."
                      value={chikitsaForm.modernTreatment}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, modernTreatment: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Pathya (Wholesome Regimen)</Label>
                    <Textarea
                      rows={2}
                      placeholder="e.g. Mudga Yusha, Lajamanda, Warm water"
                      value={chikitsaForm.pathya}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, pathya: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Apathya (Prohibitions)</Label>
                    <Textarea
                      rows={2}
                      placeholder="e.g. Heavy foods, Day sleeping, Cold exposure"
                      value={chikitsaForm.apathya}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, apathya: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs font-bold">Reference Samhita</Label>
                    <Input
                      placeholder="e.g. Charaka Samhita Chikitsa Sthana Chapter 3"
                      value={chikitsaForm.referenceSamhita}
                      onChange={(e) => setChikitsaForm({ ...chikitsaForm, referenceSamhita: e.target.value })}
                    />
                  </div>

                  <div className="flex items-center gap-2 sm:col-span-2 pt-2">
                    <Switch
                      checked={chikitsaForm.isPopular}
                      onCheckedChange={(c) => setChikitsaForm({ ...chikitsaForm, isPopular: c })}
                      id="chikitsa-popular"
                    />
                    <Label htmlFor="chikitsa-popular" className="text-xs font-medium cursor-pointer">
                      Mark as "High Yield Clinical Protocol for BAMS & Clinics"
                    </Label>
                  </div>
                </div>
              )}

              <DialogFooter className="pt-4 border-t flex items-center justify-between">
                <div className="flex gap-2">
                  {chikitsaTab !== "basic" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (chikitsaTab === "modern") setChikitsaTab("shaman");
                        else if (chikitsaTab === "shaman") setChikitsaTab("treatment");
                        else if (chikitsaTab === "treatment") setChikitsaTab("basic");
                      }}
                    >
                      Previous Tab
                    </Button>
                  )}
                  {chikitsaTab !== "modern" && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        if (chikitsaTab === "basic") setChikitsaTab("treatment");
                        else if (chikitsaTab === "treatment") setChikitsaTab("shaman");
                        else if (chikitsaTab === "shaman") setChikitsaTab("modern");
                      }}
                    >
                      Next Tab
                    </Button>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsChikitsaDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isCreatingChikitsa || isUpdatingChikitsa} className="font-bold bg-rose-600 hover:bg-rose-700 text-white">
                    {editingChikitsa ? "Update Protocol" : "Save Chikitsa Protocol"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
